/** The URL's host, or null when it is not a parseable absolute URL. */
const hostOf = (src: string): string | null => {
    try {
        return new URL(src).hostname.toLowerCase();
    } catch {
        return null;
    }
};

/** The host itself, or a subdomain of it — never a domain that merely ends in it. */
const isHost = (host: string, domain: string): boolean =>
    host === domain || host.endsWith(`.${domain}`);

/**
 * Turn a YouTube/Vimeo watch URL into an autoplaying embed URL.
 *
 * Plain iframes keep these surfaces dependency-free — react-player's lazy/main
 * entries don't interop cleanly under this Vite build.
 *
 * Autoplay is safe here because nothing reaches an embed until the visitor
 * clicks play; the parameter only saves them a second click inside the iframe.
 *
 * Returns null for anything unrecognised, so callers can decline to render a
 * player rather than framing an arbitrary URL.
 */
export const toEmbedUrl = (src: string): string | null => {
    const trimmed = src.trim();

    // Anchored on the host. Unanchored, any URL merely containing "vimeo.com"
    // or a "?v=" pair produced an embed for whatever id sat next to it, so an
    // unrelated link silently became a player instead of declining.
    const host = hostOf(trimmed);
    if (!host) {
        return null;
    }

    // Vimeo's unlisted links carry a hash after the ID (vimeo.com/<id>/<hash>),
    // which the player needs as ?h= or it refuses to load the video.
    const vimeo = isHost(host, 'vimeo.com')
        ? trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/(\w+))?/)
        : null;
    if (vimeo) {
        const hash = vimeo[2] ? `h=${vimeo[2]}&` : '';
        return `https://player.vimeo.com/video/${vimeo[1]}?${hash}autoplay=1`;
    }

    const youtube =
        isHost(host, 'youtube.com') || isHost(host, 'youtu.be')
            ? trimmed.match(
                  /(?:youtu\.be\/|\/shorts\/|\/embed\/|[?&]v=)([\w-]+)/,
              )
            : null;
    if (youtube) {
        return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1&rel=0`;
    }

    return null;
};
